import React, { memo, useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import getStatusBadge, { getTagsColor } from 'helpers/getStatusBadge';
import { BADGES_COLOR_TYPES, SimpleBadge } from 'components/Shared/Badge';
import { renderLocalizedData } from 'helpers/localize';
import { camelCase } from 'lodash/string';
import { RESOURCE_ICON_LIST_FOR_TABLE } from '../../../../Resources/constants';
import ViewedTableCell from './CellContent/ViewedTableCell';
import { compare } from 'fast-json-patch';
import InfoPaneWrapper from './CellContent/InfoPaneWrapper';
import OverflownText from '../../../OverflownText';

const useStyles = makeStyles((theme) => ({
  resourceType: {
    display: 'flex',
    alignItems: 'center',
  },
  resourceTypePadding: {
    width: 12,
  },
  cell: {
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    display: 'flex',
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    transition: 'background-color .3s ease-in, font-weight .3s ease-in',
    overflow: 'hidden',
    gap: 5,
    whiteSpace: 'pre-line',
  },
}));

function areEqual(prevProps, nextProps) {
  return !compare(prevProps, nextProps).length;
}

const CustomTableCell = memo((prop) => {
  const {
    dataKey,
    withBubble,
    withBubbles,
    row,
    canManageViewed,
    renderedCellValue,
    infoPanePage,
    hasManageViewed,
    selectorOptionsFromApi,
  } = prop;
  return !!hasManageViewed
    ? <ViewedTableCell
      row={row}
      canManageViewed={canManageViewed}
    >
      <_BaseTableCell
        dataKey={dataKey}
        withBubble={withBubble}
        withBubbles={withBubbles}
        row={row}
        renderedCellValue={renderedCellValue}
        infoPanePage={infoPanePage}
        selectorOptionsFromApi={selectorOptionsFromApi}
      />
    </ViewedTableCell>
    : <_BaseTableCell
      dataKey={dataKey}
      withBubble={withBubble}
      withBubbles={withBubbles}
      row={row}
      renderedCellValue={renderedCellValue}
      infoPanePage={infoPanePage}
      selectorOptionsFromApi={selectorOptionsFromApi}
    />;
}, areEqual);

const _BaseTableCell = memo(({
                               dataKey,
                               withBubble,
                               row,
                               renderedCellValue: currentValue,
                               infoPanePage,
                               withBubbles,
                               selectorOptionsFromApi,
                             }) => {
  if (withBubbles) {
    return <OverflownText data={
      currentValue.map((item) => {
        let colorType = getTagsColor(item);
        if (selectorOptionsFromApi) {
          const color = selectorOptionsFromApi ? selectorOptionsFromApi.find(i => i.value === item)?.color : null;
          colorType = BADGES_COLOR_TYPES[color];
        }
        return <SimpleBadge label={item}
                            colorType={colorType}
                            variant={'default'}
        />;
      })
    } />;
  }

  if (infoPanePage) {
    return <InfoPaneWrapper
      currentValue={currentValue}
      row={row}
      infoPanePage={infoPanePage}
    />;
  }

  const classes = useStyles();
  const isStatus = useMemo(() => dataKey.toLowerCase().includes('status'), [dataKey]);
  const withIcon = dataKey === 'resourceName';

  const isNonTextCell = isStatus || withIcon || withBubble;

  if (!isNonTextCell) {
    return <div className={classes.cell}>
      {currentValue}
    </div>;
  }

  const data = useMemo(() => {
    if (isStatus || withBubble) {
      return <SimpleBadge label={currentValue}
                          colorType={isStatus ? getStatusBadge(camelCase(currentValue)) : getStatusBadge(currentValue)}
      />;
    } else if (withIcon) {
      return <div className={classes.resourceType}>
        {RESOURCE_ICON_LIST_FOR_TABLE[currentValue.resourceType]}
        <div className={classes.resourceTypePadding} />
        {renderLocalizedData(currentValue.resourceName ?? '-')}
      </div>;
    }
  }, [currentValue]);

  return useMemo(() => <div className={classes.cell}>
      {data}
    </div>,
  );
});

export default CustomTableCell;
